import {
  SkyAccordion,
  SkyAccordionItem,
  SkyBadge,
  SkyButton,
  SkyMessageBar,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { tableNoRows, tablesLoadedSentence } from "@shared/runs/noRows";
import { useLayoutEffect, useMemo, useRef, useState, type ComponentPropsWithoutRef } from "react";
import type { DbtCauseView } from "@shared/types/dbtCauses";
import type { RunDetail, RunStepRow, StepName } from "@shared/types/runs";
import { CauseBlock } from "@/components/transformations/CauseBlock";
import { RetryIcon } from "@/lib/icons";
import { namesItsConnection, type ActivityStep } from "@/lib/runs/runActivity";
import { attemptsSentence, type StepAttemptHistory } from "@/lib/runs/stepAttempts";
import { ImportCauseBlock, type ImportCauseHandlers } from "./ImportCauseBlock";
import { stepRetryOffered } from "./retryTargets";
import {
  STEP_CANCELLED_TEXT,
  cancelledByWording,
  formatCount,
  stepMessageIntent,
  stepMessageTitle,
  stepStatusPresentation,
  stepStatusWord,
  transformLineParts,
} from "./runFormatting";
import { formatInstant } from "@/lib/time/displayTime";
import { RunningDuration } from "./RunningDuration";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * Step details under the run page's timeline (Session 10, P11; ux-s10.md §1): one
 * `SkyAccordionItem` per step row of THIS run (never the five step names), the running or failed
 * step open. Each holds the attempt, when it ran, the step's sanitised `error` and its table or
 * model counts, and the per-step "Retry the … step" for a reader with `runs.retry`.
 *
 * The heading is what a screen reader walks: the step's position and label, its status as a word
 * and a colour, the attempt and the duration; the running step carries `aria-current="step"`.
 *
 * The error text is rendered verbatim. `runner/steps/sanitiseError.ts` has already reduced it
 * (quoted literals removed, length bounded): a warehouse cast error quotes the offending source
 * row, and this deployment's premise is that no PHI reaches the platform. Never re-expand it.
 * The bar does not announce (its live attributes are removed, as the callout's are): the page's
 * one polite line already says what happened.
 *
 * A failed transform step also shows its classified cause with `CauseBlock` (contract §12): the
 * label, the identifiers dbt's failure named and "Why no error text?". The view is the server's
 * (`GET /runs/:runId/transform-results`, `cause`); this component never composes one.
 *
 * Product gaps row 4: an extract or load step that recorded a classified cause (a failed step, or
 * a partial extract's skipped tables) shows it with `ImportCauseBlock`: the label, what
 * happened, the next step and its one button. The view is the server's (`RunStepRow.cause`).
 */

function QuietBar(props: ComponentPropsWithoutRef<typeof SkyMessageBar>) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    ref.current?.removeAttribute("role");
    ref.current?.removeAttribute("aria-live");
  });
  return <SkyMessageBar {...props} ref={ref} />;
}

function rowKey(row: Pick<RunStepRow, "dataflowId" | "step">): string {
  return `${row.dataflowId ?? ""}::${row.step}`;
}

/** Open by default: a step that is running, failed, or carries an error to read. */
function opensByDefault(row: RunStepRow): boolean {
  return row.status === "running" || row.status === "failed" || Boolean(row.error);
}

/** "12 of 40 tables extracted" / "38 of 40 tables loaded" / "4 models · 23 tests passed". */
function countsLine(row: RunStepRow, run: RunDetail): string | null {
  // #1102: a step counts its own connection's tables. A row with no connection has none.
  const entities =
    row.dataflowId === null
      ? []
      : run.entities.filter((entity) => entity.dataflowId === row.dataflowId);
  if (row.step === "extract" && entities.length > 0) {
    const done = entities.filter((entity) => entity.status === "succeeded").length;
    return `${formatCount(done)} of ${formatCount(entities.length)} tables extracted`;
  }
  if (row.step === "load" && entities.some((entity) => entity.load)) {
    // #2392: a table this import brought no row for is counted apart, never as loaded.
    const noRows = entities.filter((entity) => tableNoRows(entity) !== null).length;
    const loaded = entities.filter(
      (entity) => entity.load?.status === "succeeded" && tableNoRows(entity) === null,
    ).length;
    // #1127: a planned table the extract never reported was never given to the load.
    const given = entities.filter((entity) => entity.status !== "waiting").length;
    return tablesLoadedSentence({ loaded, noRows, given });
  }
  if (row.step === "transform") {
    const parts = transformLineParts(run.transformTotals);
    return parts.length > 0 ? parts.map((part) => part.text).join(" · ") : null;
  }
  return null;
}

/** A step's accordion heading: position and label, status, attempt and duration. */
function StepHeading({
  row,
  item,
  index,
  id,
  repeatedCount,
  runTerminal,
  attemptsLine,
}: {
  /** #1318: "Attempt 1 failed, attempt 2 succeeded." for a retried step; null otherwise. */
  attemptsLine: string | null;
  row: RunStepRow;
  item: ActivityStep;
  index: number;
  id: string;
  /** How many rows of this run share the step's name; above one, the connection is named. */
  repeatedCount: number;
  runTerminal: boolean;
}) {
  const presentation = stepStatusPresentation(row.status);
  const notStarted = row.status === "pending";
  return (
    <span
      className="flex flex-wrap items-center gap-2"
      aria-current={row.status === "running" ? "step" : undefined}
      data-testid={`run-step-${id}`}
    >
      <SkyText size={300} weight="semibold">
        {`${index + 1}. ${item.label}`}
        {item.dataflowName && repeatedCount > 1 && !namesItsConnection(item)
          ? ` (${item.dataflowName})`
          : ""}
      </SkyText>
      <SkyBadge appearance="tint" color={presentation.color}>
        {stepStatusWord(row.status, runTerminal)}
      </SkyBadge>
      <SkyText size={200} color="muted" testId={`run-step-attempts-${id}`}>
        {attemptsLine ?? (notStarted ? "Not started" : `Attempt ${row.attempt}`)}
      </SkyText>
      {notStarted ? null : (
        <SkyText size={200} color="muted" font="numeric">
          <RunningDuration
            seconds={row.durationSeconds}
            runningSince={row.status === "running" ? row.startedAt : null}
          />
        </SkyText>
      )}
    </span>
  );
}

/** "Started … · Finished …", or that the step has not started. */
function whenLine(row: RunStepRow, zone: string): string {
  if (row.status === "pending") return "This step has not started.";
  const finished = row.finishedAt
    ? ` · Finished ${formatInstant(row.finishedAt, zone, "seconds")}`
    : "";
  return `Started ${formatInstant(row.startedAt, zone, "seconds")}${finished}`;
}

/** An extract or load step's classified cause, with the page's action when it has one. */
function StepImportCause({
  cause,
  dataflowId,
  id,
  causes,
  offersRetry,
}: {
  cause: NonNullable<RunStepRow["cause"]>;
  dataflowId: string | null;
  id: string;
  causes: ImportCauseHandlers | undefined;
  offersRetry: boolean;
}) {
  return (
    <ImportCauseBlock
      cause={cause}
      action={causes ? causes.action(cause, dataflowId, { stepRetryShown: offersRetry }) : null}
      permitted={causes ? causes.permitted(cause) : false}
      {...(causes?.askLine ? { askLine: causes.askLine(cause) } : {})}
      testId={`run-step-import-cause-${id}`}
    />
  );
}

/** A step's panel: when it ran, its counts, its error or cause, and its Retry. */
function StepPanel({
  row,
  id,
  zone,
  counts,
  transformCause,
  causes,
  offersRetry,
  onRetry,
  cancelledBy,
  label,
}: {
  row: RunStepRow;
  /** The step's heading name, so its message reads the same (#1202). */
  label: string;
  /** #982: who cancelled the run, to name in a force-cancelled step's text. */
  cancelledBy?: string | undefined;
  id: string;
  zone: string;
  counts: string | null;
  transformCause: DbtCauseView | null;
  causes: ImportCauseHandlers | undefined;
  offersRetry: boolean;
  onRetry: (step: StepName, dataflowId: string | null) => void;
}) {
  // The run-level label only for a failed step; a skipped load's retry is not "from failed".
  const retryText = row.status === "failed" ? "Retry from failed step" : "Retry this step";
  return (
    <div className="flex flex-col gap-2" data-testid={`run-step-panel-${id}`}>
      <SkyText size={200} color="muted" block>
        {whenLine(row, zone)}
      </SkyText>
      {counts ? (
        <SkyText size={200} block testId={`run-step-counts-${id}`}>
          {counts}
        </SkyText>
      ) : null}
      {row.error ? (
        <QuietBar
          intent={stepMessageIntent(row.status)}
          layout="multiline"
          title={stepMessageTitle(row.step, row.status, label)}
          // Verbatim. The runner already reduced this text; there is no longer
          // version to reveal.
          message={
            // #982: the stored text stays (retry reads it); the page names the person.
            row.status === "cancelled" && row.error === STEP_CANCELLED_TEXT && cancelledBy
              ? `Cancelled by ${cancelledByWording(cancelledBy)}`
              : row.error
          }
          testId={`run-step-message-${id}`}
        />
      ) : null}
      {row.step === "transform" && transformCause ? (
        <CauseBlock cause={transformCause} whyOnlyWhenGeneric testId={`run-step-cause-${id}`} />
      ) : null}
      {(row.step === "extract" || row.step === "load") && row.cause ? (
        <StepImportCause
          cause={row.cause}
          dataflowId={row.dataflowId}
          id={id}
          causes={causes}
          offersRetry={offersRetry}
        />
      ) : null}
      {offersRetry ? (
        <div>
          <SkyButton
            appearance="secondary"
            size="small"
            icon={RetryIcon}
            onClick={() => onRetry(row.step, row.dataflowId)}
            aria-label={`${retryText}: ${label}`}
            testId={`run-retry-${id}`}
          >
            {retryText}
          </SkyButton>
        </div>
      ) : null}
    </div>
  );
}

export interface RunStepDetailsProps {
  run: RunDetail;
  /** The timeline's steps, in its order (`deriveRunActivity`'s `steps`). */
  steps: readonly ActivityStep[];
  canRetry: boolean;
  /** A force-reset or cancelled run offers no per-step retry (`retryTargets.ts`). */
  retryBlocked: boolean;
  runTerminal: boolean;
  onRetry: (step: StepName, dataflowId: string | null) => void;
  /** The failed transform step's classified cause, as the server built it; null when none. */
  transformCause?: DbtCauseView | null;
  /**
   * Product gaps row 4: the page's action for an import cause, and whether this reader may take
   * it. Absent, a cause block shows its fix and no button.
   */
  causes?: ImportCauseHandlers;
  /** #1318: how each earlier attempt of a retried step ended, from the run's events. */
  attempts?: StepAttemptHistory;
}

export function RunStepDetails({
  run,
  steps,
  canRetry,
  retryBlocked,
  runTerminal,
  onRetry,
  transformCause = null,
  causes,
  attempts,
}: RunStepDetailsProps) {
  const zone = useDisplayTz();
  const rows = useMemo(() => {
    const byKey = new Map(run.steps.map((row) => [rowKey(row), row]));
    return steps.flatMap((step) => {
      const row = byKey.get(rowKey(step));
      return row ? [{ item: step, row }] : [];
    });
  }, [run.steps, steps]);

  // A step name that appears once keeps its plain test id; a run of several connections names
  // the connection too.
  const repeated = useMemo(() => {
    const seen = new Map<StepName, number>();
    for (const { row } of rows) seen.set(row.step, (seen.get(row.step) ?? 0) + 1);
    return seen;
  }, [rows]);
  const idOf = (row: RunStepRow): string =>
    (repeated.get(row.step) ?? 0) > 1 && row.dataflowId
      ? `${row.step}-${row.dataflowId}`
      : row.step;

  // Controlled: a step opens by default while it is running or once it failed, and a reader's own
  // toggle wins from then on, so a live update never snaps shut what someone opened.
  const [toggled, setToggled] = useState<ReadonlyMap<string, boolean>>(new Map());
  const openItems = rows
    .filter(({ row }) => toggled.get(rowKey(row)) ?? opensByDefault(row))
    .map(({ row }) => rowKey(row));

  const extractStatusOf = (dataflowId: string | null) =>
    run.steps.find((row) => row.step === "extract" && row.dataflowId === dataflowId)?.status;

  if (rows.length === 0) {
    return (
      <SkyText size={200} color="muted" block testId="run-step-details-empty">
        This import has no steps yet.
      </SkyText>
    );
  }

  return (
    <div data-testid="run-step-details">
      <SkyText as="h3" size={300} weight="semibold" block className="mb-1">
        Step details
      </SkyText>
      <SkyAccordion
        multiple
        openItems={openItems}
        onToggle={(value, open) => {
          setToggled((previous) => {
            const next = new Map(previous);
            next.set(value, open.includes(value));
            return next;
          });
        }}
        testId="run-step-accordion"
      >
        {rows.map(({ item, row }, index) => {
          const id = idOf(row);
          const offersRetry =
            canRetry &&
            stepRetryOffered(row.step, row, extractStatusOf(row.dataflowId), retryBlocked);
          return (
            <SkyAccordionItem
              key={rowKey(row)}
              value={rowKey(row)}
              heading={
                <StepHeading
                  row={row}
                  item={item}
                  index={index}
                  id={id}
                  repeatedCount={repeated.get(row.step) ?? 0}
                  runTerminal={runTerminal}
                  attemptsLine={attempts ? attemptsSentence(attempts, row, runTerminal) : null}
                />
              }
            >
              <StepPanel
                row={row}
                label={item.label}
                id={id}
                zone={zone}
                counts={countsLine(row, run)}
                transformCause={transformCause}
                causes={causes}
                offersRetry={offersRetry}
                onRetry={onRetry}
                cancelledBy={run.cancelledBy}
              />
            </SkyAccordionItem>
          );
        })}
      </SkyAccordion>
    </div>
  );
}
